import {ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    BellOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    EditOutlined,
    EnvironmentOutlined,
    SearchOutlined
} from '@ant-design/icons';
import {useCandidateCount, useCandidatePool} from "./hooks/useCandidatePool";
import styled from "styled-components";
import {colors} from "../../util/colors";
import {Button, message, Modal, Radio, Skeleton, Spin, Tooltip} from "antd";
import {VariableSizeList as VirtualList} from 'react-window';
import InfiniteLoader from "react-window-infinite-loader";
import {IJobDemand, IJobDetails, ILocation, IPosition} from "../../services/types";
import {addJobDemand, updateSearchById} from "../../services/api";
import {AuthContext} from "../../context/AuthContext.tsx";
import {ProfessionModal} from "../../components/ProfessionModal.tsx";
import {AddressModal} from "../../components/AddressModal.tsx";
import {useMutation} from "@tanstack/react-query";
import {alertOptions} from "../../util/utils.ts";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {TableRow} from "./TableRow.tsx";
import {CandidateItem} from "./components/CandidateItem.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import {
    InputLabels,
    SaveFilter,
    SearchContainer,
    StyledCloseBtn,
    StyledInput,
    TableWrapper
} from "candidate/src/pages/jobPool/JobPool.tsx";
import {safelyUnmountList} from "./utils";

const LIMIT = 20;

export const CandidatePool = () => {
    // Use refs for mutable values that don't trigger re-renders
    const listRef = useRef<VirtualList | null>(null);
    const infiniteLoaderRef = useRef<InfiniteLoader | null>(null); // Ref for InfiniteLoader itself if needed
    const initialLoadRef = useRef(true); // To prevent saving scroll on initial mount/load
    const sizeMap = useRef<{ [key: number]: number }>({});
    const isNavigating = useRef(false); // Flag to manage navigation state

    // State
    const { user, isNewUserBannerVisible, setBannerVisible } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const isPrivateUser = user?._client?.isPrivate;

    // --- Search State ---
    const [whatSelectedValue, setWhatSelectedValue] = useState<IPosition | null>(null);
    const [whereSelectedValue, setWhereSelectedValue] = useState<ILocation | null>(null);
    const [isWhatModalOpen, setIsWhatModalOpen] = useState(false);
    const [isLocationEditorOpen, setIsLocationEditorOpen] = useState(false);

    // --- Alert State ---
    const [selectedAlertOption, setSelectedAlertOption] = useState(alertOptions[0]);
    const [savedSearchItem, setSavedSearchItem] = useState<IJobDemand | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    // --- Virtual List State ---
    // containerHeight depends on window size, managed by effect
    const [containerHeight, setContainerHeight] = useState(window.innerHeight - 176);
    // scrollPosition is persisted in sessionStorage
    const [scrollPosition, setScrollPosition] = useState(() => {
        const saved = sessionStorage.getItem('candidatePoolScrollPosition');
        return saved ? parseInt(saved, 10) : 0;
    });

    // --- Data Fetching ---
    const {
        data,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useCandidatePool(LIMIT, whatSelectedValue || undefined, whereSelectedValue || undefined);


    const {
        data: numberOfCandidate,
        isLoading: isLoadingCount // Renamed to avoid conflict
    } = useCandidateCount(whatSelectedValue || undefined, whereSelectedValue || undefined);

    // Derived Data
    const flattenedData = useMemo(() => {
        if (!data || (data?.pages.length === 1 && Array.isArray(data.pages[0]) && data.pages[0][0]?.id === 0)) {
            return [];
        }
        return data.pages.flatMap(page => Array.isArray(page) ? page : []).filter(el => !!el?.registered_at);
    }, [data]);

    const itemCount = hasNextPage ? flattenedData.length + 1 : flattenedData.length;

    // --- Effects ---

    // Effect for parsing URL params on mount and location change
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchWhatIdParam = Number(params.get('search_what_id'));
        const searchWhatLabel = params.get('search_what_label');
        const searchLocationIdParam = Number(params.get('search_location_id'));
        const searchLocationLabel = params.get('search_location_label');

        let needsReset = false;

        const newWhat = (searchWhatIdParam && searchWhatLabel)
            ? { id: searchWhatIdParam, label: searchWhatLabel } as IPosition
            : null;
        if (JSON.stringify(newWhat) !== JSON.stringify(whatSelectedValue)) {
            setWhatSelectedValue(newWhat);
            needsReset = true;
        }

        const newWhere = (searchLocationIdParam && searchLocationLabel)
            ? { id: searchLocationIdParam, label: searchLocationLabel } as ILocation
            : null;
        if (JSON.stringify(newWhere) !== JSON.stringify(whereSelectedValue)) {
            setWhereSelectedValue(newWhere);
            needsReset = true;
        }

        if (needsReset && !location.state?.fromCandidateDetails) {
            setScrollPosition(0);
            sessionStorage.removeItem('candidatePoolScrollPosition');
            listRef.current?.scrollTo(0);
            initialLoadRef.current = true;
            if (infiniteLoaderRef.current) {
                infiniteLoaderRef.current?.resetloadMoreItemsCache();
            }
        }
        setTimeout(() => { initialLoadRef.current = false; }, 0);
    }, [location.search, location.state?.fromCandidateDetails, whatSelectedValue, whereSelectedValue]);

    // Effect for handling window resize
    useEffect(() => {
        const handleResize = () => {
            setContainerHeight(window.innerHeight - 176);
            if (listRef.current) {
                listRef.current?.resetAfterIndex(0);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Effect for general component mount/unmount logic and list cleanup
    useEffect(() => {
        isNavigating.current = false;

        const savedScrollPosition = sessionStorage.getItem('candidatePoolScrollPosition');
        if (savedScrollPosition && listRef.current) {
            const parsedPosition = parseInt(savedScrollPosition, 10);
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current?.scrollTo(parsedPosition);
                    setScrollPosition(parsedPosition);
                    initialLoadRef.current = false;
                }
            }, 100);
        } else {
            initialLoadRef.current = false;
        }

        const storedTarget = localStorage.getItem('redirectPath');
        if (storedTarget && storedTarget !== "/") {
            localStorage.removeItem("redirectPath");
            navigate(storedTarget, { replace: true });
        }

        return () => {
            safelyUnmountList(listRef);
        };
    }, [navigate]);

    useEffect(() => {
        if (location.state?.fromCandidateDetails) {
            const savedScrollPosition = sessionStorage.getItem('candidatePoolScrollPosition');
            if (savedScrollPosition) {
                const parsedPosition = parseInt(savedScrollPosition, 10);
                setScrollPosition(parsedPosition);
                listRef.current?.scrollTo(parsedPosition);
            }
        } else {
            setScrollPosition(0);
            sessionStorage.removeItem('candidatePoolScrollPosition');
        }
    }, [location, whatSelectedValue, whereSelectedValue]);

    // --- Callbacks ---
    const handleScroll = useCallback(({ scrollOffset }: { scrollOffset: number }) => {
        // Only save scroll position if not initializing and not during navigation away
        if (!initialLoadRef.current && !isNavigating.current) {
            setScrollPosition(scrollOffset);
            sessionStorage.setItem('candidatePoolScrollPosition', scrollOffset.toString());
        }
    }, []);

    const loadMoreItems = useCallback(async (): Promise<void> => {
        if (isFetchingNextPage || !hasNextPage) {
            return; // Return void explicitly or implicitly
        }
        await fetchNextPage(); // Await the promise, effectively returning Promise<void>
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    const isItemLoaded = useCallback((index: number) => !hasNextPage || index < flattenedData.length,
        [hasNextPage, flattenedData.length]
    );

    const navigateWithParams = useCallback((params: URLSearchParams) => {
        isNavigating.current = true;
        const searchString = params.toString();
        // Reset scroll immediately before navigation triggers potential unmount
        sessionStorage.removeItem('candidatePoolScrollPosition');
        setScrollPosition(0);
        initialLoadRef.current = true;
        navigate(`/candidates?${searchString}`, { state: { fromCandidateDetails: false } }); // Clear from details state
    }, [navigate]);

    const handleSaveProfession = useCallback((value: IPosition | null) => {
        setIsWhatModalOpen(false); // Close modal immediately

        // Avoid navigation if the value hasn't actually changed
        if (value?.id === whatSelectedValue?.id) {
            return;
        }

        setWhatSelectedValue(value); // Update state

        const params = new URLSearchParams(location.search);
        if (value?.id) {
            params.set('search_what_id', value.id.toString());
            params.set('search_what_label', value.label);
        } else {
            params.delete('search_what_id');
            params.delete('search_what_label');
        }
        navigateWithParams(params);

    }, [location.search, navigateWithParams, whatSelectedValue?.id]); // whatSelectedValue object is unstable, use id

    const handleSaveLocation = useCallback((value: ILocation | null) => {
        setIsLocationEditorOpen(false); // Close modal immediately

        // Avoid navigation if the value hasn't actually changed
        if (value?.id === whereSelectedValue?.id) {
            return;
        }

        setWhereSelectedValue(value); // Update state

        const params = new URLSearchParams(location.search);
        if (value?.id) {
            // Ensure label_client exists before setting
            params.set('search_location_label', value.label_client || value.label || ''); // Use fallback label if needed
            params.set('search_location_id', value.id.toString());
        } else {
            params.delete('search_location_label');
            params.delete('search_location_id');
        }
        navigateWithParams(params);

    }, [location.search, navigateWithParams, whereSelectedValue?.id]); // whereSelectedValue object is unstable, use id

    // Direct clear functions
    const clearWhatFilter = useCallback(() => {
        if (whatSelectedValue) { // Only navigate if there's something to clear
            setWhatSelectedValue(null);
            const params = new URLSearchParams(location.search);
            params.delete('search_what_id');
            params.delete('search_what_label');
            navigateWithParams(params);
        }
    }, [whatSelectedValue, location.search, navigateWithParams]);

    const clearWhereFilter = useCallback(() => {
        if (whereSelectedValue) { // Only navigate if there's something to clear
            setWhereSelectedValue(null);
            const params = new URLSearchParams(location.search);
            params.delete('search_location_id');
            params.delete('search_location_label');
            navigateWithParams(params);
        }
    }, [whereSelectedValue, location.search, navigateWithParams]);


    // --- Alert/Save Search Mutation ---
    const { mutate: saveSearchMutation, isPending: isSavingSearch } = useMutation({
        mutationFn: (jobDemand: Omit<IJobDemand, 'id' | 'client_id'>) => addJobDemand(jobDemand as IJobDemand),
        onSuccess: (response) => {
            setSavedSearchItem(response);
            setIsAlertModalOpen(true);
            message.success('Suche gespeichert. Richten Sie nun Benachrichtigungen ein.');
        },
        onError: (error: {message?: string}) => {
            console.error('Error saving filter:', error);
            message.error(`Speichern fehlgeschlagen: ${error?.message || 'Bitte versuchen Sie es erneut.'}`);
        },
    });

    const { mutate: updateSearchMutation, isPending: isUpdatingSearch } = useMutation({
        mutationFn: (updatedData: Partial<IJobDetails>) => updateSearchById(savedSearchItem?.id ?? 0, updatedData),
        onSuccess: () => {
            message.success('Benachrichtigungseinstellungen gespeichert');
            setIsAlertModalOpen(false);
            setSavedSearchItem(null); // Clear temp item after saving
        },
        onError: (error: {message?: string}) => { // Added type
            message.error(`Update fehlgeschlagen: ${error?.message ?? 'Bitte versuchen Sie es erneut.'}`);
        },
    });

    const handleSaveFilterClick = useCallback(() => {
        if (user?.id && whatSelectedValue?.id) {
            const jobDemand = {
                type: "candidate_search",
                search_what_id: whatSelectedValue.id,
                search_location_id: whereSelectedValue?.id || null
            };
            saveSearchMutation(jobDemand as IJobDemand);
        } else if (!whatSelectedValue?.id) {
            message.warning('Bitte wählen Sie zuerst aus, was Sie suchen (Beruf/Tätigkeit).');
        } else {
            message.error('Fehler: Benutzer nicht gefunden.');
        }
    }, [user?.id, whatSelectedValue, whereSelectedValue?.id, saveSearchMutation]);

    const handleSaveAlertSettings = () => {
        if (savedSearchItem?.id) { // Check if we have the temporary saved item ID
            updateSearchMutation({
                // Only send fields relevant to the alert update
                alert_label: selectedAlertOption.label,
                alert_config_id: selectedAlertOption.id
            });
        } else {
            message.error("Fehler: Gespeicherte Suche nicht gefunden zum Aktualisieren.")
        }
    };

    // --- Virtual List Size Management ---
    const setSize = useCallback((index: number, size: number) => {
        const oldSize = sizeMap.current[index];
        if (size !== oldSize) {
            // console.log(`Setting size for index ${index} to ${size}`);
            sizeMap.current = { ...sizeMap.current, [index]: size };
            // Only reset if the list ref exists
            if (listRef.current) {
                listRef.current.resetAfterIndex(index);
            }
        }
    }, []); // No dependencies needed

    const getSize = useCallback((index: number) => sizeMap.current[index] || 100, []); // Default size

    const onWhatLooking = () => {
        setIsWhatModalOpen(true);
    };

    const onWhereLooking = useCallback(() => {
        setIsLocationEditorOpen(true);
    }, []);

    const onDirectWhatChange = useCallback((value: string | null) => {
        const params = new URLSearchParams(location.search);
        setWhatSelectedValue(null)
        if (!value) {
            params.delete('search_what_id');
            params.delete('search_what_label');
            setWhatSelectedValue(null);

            navigate(`/candidates?${params.toString()}`);
        }
    }, [navigate, location.search]);

    const onDirectWhereChange = useCallback((value: string | null) => {
        const params = new URLSearchParams(location.search);
        setWhereSelectedValue(null)
        if (!value) {
            params.delete('search_location_id');
            params.delete('search_location_label');
            setWhereSelectedValue(null);

            navigate(`/candidates?${params.toString()}`);
        }
    }, [navigate, location.search]);

    return (
        <Wrapper>
            <SearchContainer>
                <InputWrapper>
                    <InputLabels>Was?</InputLabels>
                    <StyledInput
                        readOnly={true}
                        onClick={onWhatLooking}
                        placeholder={isMobile ? "Was?" : "Alle, kein Filter"}
                        value={whatSelectedValue?.label || ''}
                        prefix={isMobile ? <SearchOutlined/> : <EditOutlined/>}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDirectWhatChange(e.target.value)}
                        suffix={
                            <StyledCloseBtn
                                onClick={(e) => { e.stopPropagation(); clearWhatFilter(); }}
                                style={{color: colors.text, backgroundColor: "white"}}
                                size="small"
                                shape="circle"
                                icon={<CloseOutlined/>}
                            />
                        }
                        size="large"
                    />
                </InputWrapper>
                <InputWrapper>
                    <InputLabels>Wo?</InputLabels>
                    <StyledInput
                        size="large"
                        onClick={onWhereLooking}
                        readOnly={true}
                        placeholder={isMobile ? "Wo?" : "Alle, kein Filter"}
                        value={whereSelectedValue?.label || ''}
                        prefix={isMobile ? <EnvironmentOutlined/> : <EditOutlined/>}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDirectWhereChange(e.target.value)}
                        suffix={
                            <StyledCloseBtn
                                onClick={(e) => { e.stopPropagation(); clearWhereFilter(); }}
                                style={{color: colors.text, backgroundColor: "white"}}
                                size="small"
                                shape="circle"
                                icon={<CloseOutlined/>}
                            />
                        }
                    />
                </InputWrapper>
                <Tooltip
                    title="Wählen Sie zuerst welche Berufe oder Tätigkeiten Sie suchen, bevor Sie eine Suche speichern">
                    <SaveFilter onClick={handleSaveFilterClick}
                                disabled={!whatSelectedValue?.id || isSavingSearch}>
                        <BellOutlined/>Suche speichern
                    </SaveFilter>
                </Tooltip>
            </SearchContainer>
            <CandidateCount>
                Kandidatenpool: {isLoadingCount ? (
                <Skeleton.Button active={true} style={{width: 128, height: 16}} size='small' shape='default'/>
            ) : (
                `(${numberOfCandidate?.id || 0} Kandidaten)`
            )}
            </CandidateCount>
            {isFetching && <AbsLoader><Spin size="large"/></AbsLoader>}
            {flattenedData.length>0 && <TableWrapper>
                <InfiniteLoader
                    ref={infiniteLoaderRef} // Assign ref
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems}
                    threshold={10} // Optional: How many items from the end triggers loadMoreItems
                >
                    {({ onItemsRendered, ref }) => (
                        <VirtualList
                            key={whatSelectedValue?.id + '-' + whereSelectedValue?.id} // Force remount on filter change
                            ref={(list) => {
                                // Assign to both InfiniteLoader's ref and our local ref
                                ref(list);
                                listRef.current = list;
                            }}
                            height={containerHeight}
                            itemCount={itemCount}
                            itemSize={getSize}
                            onItemsRendered={onItemsRendered}
                            width="100%"
                            overscanCount={5} // Adjust as needed
                            // useIsScrolling // Enable for potential performance gains if needed
                            initialScrollOffset={scrollPosition}
                            onScroll={handleScroll}
                        >
                            {/* Row Renderer */}
                            {({ index, style }) => {
                                // Check if the item is loaded
                                if (!isItemLoaded(index)) {
                                    // Render a placeholder while loading
                                    return (
                                        <div style={style}>
                                            <Skeleton active paragraph={{ rows: 1 }} style={{ padding: '100px 400px' }} />
                                        </div>
                                    );
                                }
                                // Render the actual item
                                const item = flattenedData[index];

                                if (!item) return null; // Should not happen if isItemLoaded is correct, but safe check

                                return (
                                    <div style={style}>
                                        <TableRow
                                            index={index}
                                            setSize={setSize}
                                            flattenedData={flattenedData}
                                        >
                                            {() => <CandidateItem item={flattenedData[index]}/>}
                                        </TableRow>
                                    </div>
                                );
                            }}
                        </VirtualList>
                    )}
                </InfiniteLoader>
            </TableWrapper>}

            {/* Modals */}
            {isLocationEditorOpen && <AddressModal
                open={isLocationEditorOpen}
                onClose={() => setIsLocationEditorOpen(false)}
                onSave={handleSaveLocation}
                initialValue={whereSelectedValue}
            />}
            {isWhatModalOpen && <ProfessionModal
                initialValue={whatSelectedValue?.label || ""}
                isModalOpen={isWhatModalOpen}
                handleSaveProf={handleSaveProfession}
                onClose={() => setIsWhatModalOpen(false)}
            />}
            <Modal
                title="Benachrichtigung per Email einrichten"
                open={isAlertModalOpen}
                onCancel={() => setIsAlertModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsAlertModalOpen(false)} disabled={isUpdatingSearch}>
                        Abbrechen
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSaveAlertSettings} loading={isUpdatingSearch} disabled={isUpdatingSearch}>
                        Speichern & Schließen
                    </Button>,
                ]}
            >
                <SearchLinkWrapper>
                    <CheckCircleOutlined style={{ color: 'green', fontSize: '1.2em' }}/> {/* Icon */}
                    <p>Ihre Suche wurde gespeichert. Eine Übersicht finden Sie unter:</p>
                    <Link to="/searches">{"Mein Konto > Meine Suchen"}</Link>
                </SearchLinkWrapper>
                <p>Wie häufig möchten Sie über passende neue Kandidaten informiert werden?</p>
                <Radio.Group
                    onChange={(e) => setSelectedAlertOption(alertOptions.find(option => option.id === e.target.value) || alertOptions[0])} // Added fallback
                    value={selectedAlertOption.id}
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} // Improved layout
                >
                    {alertOptions.map(option => (
                        <Radio key={option.id} value={option.id}>{option.label}</Radio>
                    ))}
                </Radio.Group>
            </Modal>

            {isPrivateUser ? <Modal
                title="Willkommen bei silvertalent"
                open={isNewUserBannerVisible}
                onCancel={() => setBannerVisible(false)}
                footer={null}
            >
                <div style={{display: "flex", flexDirection: "column", gap: "20px"}}>
                    <ul>
                        <li><b>Großer Pool an erfahrenen Fachkräften</b> <br/>
                            Finden Sie qualifizierte Rentner aus allen Branchen - von Fachexperten bis zu motivierten Quereinsteigern
                        </li>
                        <li><b>Einfach und schnell</b> <br/>
                            Erstellen Sie Ihre Stellenanzeige in wenigen Minuten und erreichen Sie direkt interessierte Kandidaten
                        </li>
                        <li><b>Sofort starten</b> <br/>
                            Nach der Bezahlung wird Ihre Stelle sofort veröffentlicht und Sie können passende Bewerber kontaktieren
                        </li>
                    </ul>
                    <Button type="primary" onClick={() => {
                        setBannerVisible(false);
                        navigate('/new/job');
                    }}>
                        Jetzt Stelle inserieren
                    </Button>
                </div>
            </Modal> : <Modal
                title="Und so einfach funktioniert silvertalent"
                open={isNewUserBannerVisible}
                footer={null}
                onCancel={() => setBannerVisible(false)}
            >
                <div style={{display: "flex", flexDirection: "column",}}>
                    <ul>
                        <li><b>Riesigen Rentnerpool nutzen</b> <br/> Wir haben Profile aller Berufsgruppen, sowohl
                            Fachkräfte als auch Hilfskräfte und Quereinsteiger
                        </li>
                        <li><b>Stellenanzeige schalten</b> <br/> Klicken Sie im Menü auf "Meine Stellen", um Ihre Stelle
                            kostenlos zu inserieren
                        </li>
                        <li><b>Tarif auswählen</b> <br/> Um Bewerbungen einzusehen passenden Tarif wählen und bequem
                            online bezahlen
                        </li>
                    </ul>
                    <Button type="primary" onClick={() => setBannerVisible(false)}>zu den
                        Kandidaten</Button>
                </div>
            </Modal>}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: ${colors.bg};
`

const CandidateCount = styled.div`
    font-weight: bold;
    margin: 16px 0;
`

const InputWrapper = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
    align-items: center;
`;

export const AbsLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: calc(50% - 22px);
    top: calc(55%);
`;

export const SearchLinkWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 16px;

    p {
        margin: 0;
    }
`;
